// src/pages/ProfilePage.js
import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MapPin,
  Camera,
  Edit,
  Check,
  Shield,
  Trash2,
  Heart,
  ShieldOff,
  Megaphone,
  RefreshCw,
  Pencil,
  Users,
  MessageCircle,
  UserMinus,
  UserCircle,
  PenLine,
  Plus,
  X,
} from 'lucide-react';
import userService from '../services/userService';
import annonceService from '../services/annonceService';
import friendService from '../services/friendService';
import {
  profileCompletion,
  getProfileMissingItems,
  getDisplayAge,
  canAccessDating,
  calculateAge,
  timeAgo,
  formatPrice,
} from '../utils';
import {
  INTENTIONS,
  LOOKING_FOR,
  EDUCATION_LEVELS,
  LANGUAGES,
  BIO_PROMPTS,
  getAnnonceCategoryLabel,
  ANNONCE_MAX_ACTIVE,
  INTEREST_CATEGORIES,
} from '../constants';
import { Btn, Card, Badge, Select, ConfirmModal } from '../components/ui';
import { searchCities } from '../utils/geocoding';
import ActivateDatingModal from '../modals/ActivateDatingModal';
import AnnonceFormModal from '../modals/AnnonceFormModal';

const ProfilePage = ({ user, onUpdate, toast }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [showActivateDating, setShowActivateDating] = useState(false);
  const [myAnnonces, setMyAnnonces] = useState([]);
  const [editingAnnonce, setEditingAnnonce] = useState(null);
  const [showAnnonceForm, setShowAnnonceForm] = useState(false);
  const [deletingAnnonceId, setDeletingAnnonceId] = useState(null);
  const [friendProfiles, setFriendProfiles] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [removingFriendUid, setRemovingFriendUid] = useState(null);
  const [editingInterests, setEditingInterests] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState(user?.interests || []);
  const [editingBio, setEditingBio] = useState(false);
  const [bioPrompts, setBioPrompts] = useState(user?.bioPrompts || []);
  const [editingDating, setEditingDating] = useState(false);
  const [datingForm, setDatingForm] = useState({
    lookingFor: user?.lookingFor || '',
    intention: user?.intention || '',
    height: user?.height ?? '',
    education: user?.education || '',
    profession: user?.profession || '',
    languages: user?.languages || [],
  });
  const [editingPrefs, setEditingPrefs] = useState(false);
  const [prefsForm, setPrefsForm] = useState({
    ageRange: user?.datingPreferences?.ageRange || [18, 50],
    maxDistance: user?.datingPreferences?.maxDistance ?? '',
    noDistanceLimit:
      user?.datingPreferences?.maxDistance === null ||
      user?.datingPreferences?.maxDistance === undefined,
    heightRange: user?.datingPreferences?.heightRange || [150, 200],
    noHeightPref:
      user?.datingPreferences?.heightRange === null ||
      user?.datingPreferences?.heightRange === undefined,
    intentions: user?.datingPreferences?.intentions || [],
    education: user?.datingPreferences?.education || '',
  });
  const isDating = canAccessDating(user);
  const userAge = calculateAge(user?.dateOfBirth);
  const [form, setForm] = useState({
    bio: user?.bio || '',
    intention: user?.intention || '',
    city: user?.city || '',
    cityPostalCode: user?.cityPostalCode || '',
    cityLat: user?.cityLat || null,
    cityLng: user?.cityLng || null,
  });
  const [cityQuery, setCityQuery] = useState(user?.city || '');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const profileRef = useRef(null);
  const coverRef = useRef(null);
  const cityDebounceRef = useRef(null);
  const citySelectedRef = useRef(!!user?.city);

  // Autocomplete ville (debounce 300ms)
  useEffect(() => {
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    if (cityQuery.length < 3 || citySelectedRef.current) {
      setCitySuggestions([]);
      return;
    }
    cityDebounceRef.current = setTimeout(async () => {
      const results = await searchCities(cityQuery);
      setCitySuggestions(results);
      setShowCityDropdown(results.length > 0);
    }, 300);
    return () => {
      if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    };
  }, [cityQuery]);

  const selectCity = (loc) => {
    setCityQuery(loc.city);
    setShowCityDropdown(false);
    setCitySuggestions([]);
    citySelectedRef.current = true;
    setForm((prev) => ({
      ...prev,
      city: loc.city,
      cityPostalCode: loc.postcode,
      cityLat: loc.lat,
      cityLng: loc.lng,
    }));
  };

  const save = async () => {
    setLoading(true);
    const r = await userService.updateProfile(user.uid, form);
    setLoading(false);
    if (r.success) {
      onUpdate(r.user);
      setEditing(false);
      toast(t('profile.toast.profileUpdated'), 'success');
    } else {
      toast(r.error || t('profile.toast.saveError'), 'error');
    }
  };

  const upload = async (e, type) => {
    const f = e.target.files?.[0];
    if (!f || !f.type.startsWith('image/')) return;
    setLoading(true);
    toast(t('profile.toast.uploading'), 'success');
    const r = await userService.uploadPic(user.uid, f, type);
    setLoading(false);
    if (r.success) {
      onUpdate(r.user);
      toast(t('profile.toast.photoUpdated'), 'success');
    } else {
      toast(r.error, 'error');
    }
  };

  const [blockedProfiles, setBlockedProfiles] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);

  useEffect(() => {
    if (!user?.blocked?.length) {
      setBlockedProfiles([]);
      return;
    }
    const loadBlocked = async () => {
      setLoadingBlocked(true);
      const profiles = [];
      for (const id of user.blocked) {
        const p = await userService.getProfile(id);
        if (p) profiles.push(p);
      }
      setBlockedProfiles(profiles);
      setLoadingBlocked(false);
    };
    loadBlocked();
  }, [user?.blocked]);

  const handleUnblock = async (targetId) => {
    const r = await userService.unblockUser(user.uid, targetId);
    if (r.success) {
      toast(t('profile.toast.unblocked'), 'success');
      const fresh = await userService.getUser();
      if (fresh) onUpdate(fresh);
    } else {
      toast(t('profile.toast.unblockError'), 'error');
    }
  };

  // ── Mes amis ──
  useEffect(() => {
    const friends = user?.friends || [];
    if (friends.length === 0) {
      setFriendProfiles([]);
      return;
    }
    setFriendsLoading(true);
    Promise.all(friends.slice(0, 6).map((uid) => userService.getProfile(uid))).then((profiles) => {
      setFriendProfiles(profiles.filter(Boolean));
      setFriendsLoading(false);
    });
  }, [user?.friends]);

  const handleRemoveFriend = async () => {
    if (!removingFriendUid) return;
    const result = await friendService.removeFriend(removingFriendUid);
    if (result.success) {
      const name = friendProfiles.find((p) => p.uid === removingFriendUid)?.name || '';
      toast(t('profile.myFriends.removeSuccess', { name }), 'success');
      setFriendProfiles((prev) => prev.filter((p) => p.uid !== removingFriendUid));
      const fresh = await userService.getUser();
      if (fresh) onUpdate(fresh);
    } else {
      toast(result.error || t('friends.errors.generic'), 'error');
    }
    setRemovingFriendUid(null);
  };

  const handleSaveInterests = async () => {
    setLoading(true);
    const r = await userService.updateProfile(user.uid, { interests: selectedInterests });
    setLoading(false);
    if (r.success) {
      onUpdate(r.user);
      setEditingInterests(false);
      toast(t('profile.toast.profileUpdated'), 'success');
    } else {
      toast(r.error || t('profile.toast.saveError'), 'error');
    }
  };

  const handleSaveBio = async () => {
    setLoading(true);
    const r = await userService.updateProfile(user.uid, { bioPrompts });
    setLoading(false);
    if (r.success) {
      onUpdate(r.user);
      setEditingBio(false);
      toast(t('profile.toast.profileUpdated'), 'success');
    } else {
      toast(r.error || t('profile.toast.saveError'), 'error');
    }
  };

  const addBioPrompt = () => {
    if (bioPrompts.length >= 3) return;
    const usedPrompts = bioPrompts.map((bp) => bp.prompt);
    const available = BIO_PROMPTS.filter((p) => !usedPrompts.includes(p));
    if (available.length === 0) return;
    setBioPrompts([...bioPrompts, { prompt: available[0], answer: '' }]);
  };

  const removeBioPrompt = (idx) => {
    setBioPrompts(bioPrompts.filter((_, i) => i !== idx));
  };

  const handleSaveDating = async () => {
    setLoading(true);
    const data = {
      lookingFor: datingForm.lookingFor || null,
      intention: datingForm.intention || null,
      height: datingForm.height ? Number(datingForm.height) : null,
      education: datingForm.education || null,
      profession: datingForm.profession,
      languages: datingForm.languages,
    };
    const r = await userService.updateProfile(user.uid, data);
    setLoading(false);
    if (r.success) {
      onUpdate(r.user);
      setEditingDating(false);
      toast(t('profile.toast.profileUpdated'), 'success');
    } else {
      toast(r.error || t('profile.toast.saveError'), 'error');
    }
  };

  const handleSavePrefs = async () => {
    setLoading(true);
    const prefs = {
      ageRange: prefsForm.ageRange,
      maxDistance: prefsForm.noDistanceLimit ? null : Number(prefsForm.maxDistance) || null,
      heightRange: prefsForm.noHeightPref ? null : prefsForm.heightRange,
      intentions: prefsForm.intentions,
      education: prefsForm.education || null,
    };
    const r = await userService.updateProfile(user.uid, { datingPreferences: prefs });
    setLoading(false);
    if (r.success) {
      onUpdate(r.user);
      setEditingPrefs(false);
      toast(t('profile.toast.profileUpdated'), 'success');
    } else {
      toast(r.error || t('profile.toast.saveError'), 'error');
    }
  };

  const toggleLanguage = (value) => {
    setDatingForm((prev) => ({
      ...prev,
      languages: prev.languages.includes(value)
        ? prev.languages.filter((v) => v !== value)
        : [...prev.languages, value],
    }));
  };

  const togglePrefIntention = (value) => {
    setPrefsForm((prev) => ({
      ...prev,
      intentions: prev.intentions.includes(value)
        ? prev.intentions.filter((v) => v !== value)
        : [...prev.intentions, value],
    }));
  };

  const toggleInterest = (value) => {
    setSelectedInterests((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  // ── Mes annonces ──
  const loadMyAnnonces = async () => {
    const r = await annonceService.getUserAnnonces(user.uid);
    if (r.success) setMyAnnonces(r.data);
  };

  useEffect(() => {
    if (user?.uid) loadMyAnnonces();
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRenewAnnonce = async (annonce) => {
    const r = await annonceService.renewAnnonce(annonce.id, user.uid);
    if (r.success) {
      toast(t('profile.toast.annonceRenewed'), 'success');
      loadMyAnnonces();
    } else {
      toast(
        r.error === 'max_active_reached'
          ? t('profile.toast.renewMaxError')
          : t('profile.toast.renewError'),
        'error'
      );
    }
  };

  const handleDeleteAnnonce = async () => {
    if (!deletingAnnonceId) return;
    const r = await annonceService.deleteAnnonce(deletingAnnonceId, user.uid);
    setDeletingAnnonceId(null);
    if (r.success) {
      toast(t('profile.toast.annonceDeleted'), 'success');
      loadMyAnnonces();
    } else {
      toast(t('profile.toast.annonceDeleteError'), 'error');
    }
  };

  const pct = profileCompletion(user);
  const missingItems = getProfileMissingItems(user);

  return (
    <div
      className="pt-24 pb-32 lg:pb-20 container mx-auto px-4 max-w-3xl"
      onClick={() => showTooltip && setShowTooltip(null)}
    >
      <Helmet>
        <title>{t('profile.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/profile" />
      </Helmet>
      <Card className="overflow-hidden">
        {/* Cover */}
        <div
          className="h-32 sm:h-40 md:h-52 relative group cursor-pointer"
          onClick={() => coverRef.current?.click()}
          style={{
            background: user.coverPicture
              ? `url(${user.coverPicture}) center/cover`
              : 'linear-gradient(135deg,#f43f5e,#a855f7)',
          }}
        >
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 text-white text-center">
              <Camera size={28} />
              <p className="text-sm mt-1">{t('profile.changeCover')}</p>
            </div>
          </div>
          <input
            ref={coverRef}
            type="file"
            accept="image/*"
            onChange={(e) => upload(e, 'cover')}
            className="hidden"
          />
        </div>

        <div className="px-4 pb-6">
          {/* Avatar */}
          <div className="relative -mt-10 sm:-mt-12 md:-mt-16 mb-3">
            <div
              className="relative group cursor-pointer inline-block"
              onClick={() => profileRef.current?.click()}
            >
              <img
                src={user.profilePicture}
                alt={user.name}
                className="w-20 h-20 sm:w-28 sm:h-28 rounded-2xl border-4 border-white dark:border-gray-900 shadow-xl object-cover"
              />
              <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center">
                <Camera size={22} className="text-white opacity-0 group-hover:opacity-100" />
              </div>
              <input
                ref={profileRef}
                type="file"
                accept="image/*"
                onChange={(e) => upload(e, 'profile')}
                className="hidden"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap mb-4">
            {editing ? (
              <>
                <Btn v="ghost" onClick={() => setEditing(false)} data-testid="profile-btn-cancel">
                  {t('profile.actions.cancel')}
                </Btn>
                <Btn icon={Check} onClick={save} loading={loading} data-testid="profile-btn-save">
                  {t('profile.actions.save')}
                </Btn>
              </>
            ) : (
              <>
                <Btn
                  icon={Edit}
                  v="outline"
                  onClick={() => setEditing(true)}
                  data-testid="profile-btn-edit"
                >
                  {t('profile.actions.edit')}
                </Btn>
              </>
            )}
          </div>

          {/* Nom + Ville + Clan */}
          <h1 className="text-xl sm:text-2xl font-bold mb-1 dark:text-gray-100">
            {user.name}
            {getDisplayAge(user) !== null ? `, ${getDisplayAge(user)}` : ''}
          </h1>
          <div className="flex flex-wrap items-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-4">
            <span className="flex items-center gap-1">
              <MapPin size={14} className="text-rose-500" />
              {user.city}
            </span>
            {user.clan && (
              <span className="font-semibold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30 px-2.5 py-0.5 rounded-full">
                {user.clan}
              </span>
            )}
          </div>

          {/* Barre de complétion */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 mb-4">
            <div className="flex justify-between mb-1.5">
              <span className="text-sm dark:text-gray-300">{t('profile.completion.label')}</span>
              <span className="font-bold text-rose-600 dark:text-rose-400">{pct}%</span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full">
              <div
                className="h-full bg-rose-500 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            {missingItems.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {missingItems.map((item) => (
                  <span
                    key={item}
                    className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full"
                  >
                    {t(item)}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Stats — dating only */}
          {isDating && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-purple-600 dark:text-purple-400">
                  {user.likes?.length || 0}
                </p>
                <p className="text-xs text-purple-500 dark:text-purple-400">
                  {t('profile.stats.likes')}
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {user.matches?.length || 0}
                </p>
                <p className="text-xs text-blue-500 dark:text-blue-400">
                  {t('profile.stats.matches')}
                </p>
              </div>
            </div>
          )}

          {/* Mode édition / lecture */}
          {editing ? (
            <div className="space-y-4 bg-gray-50 dark:bg-gray-800 rounded-2xl p-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5 dark:text-gray-300">
                  {t('profile.edit.bio')}
                </label>
                <textarea
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  className="w-full px-3 py-2.5 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 resize-none focus:outline-none transition"
                  rows={3}
                  maxLength={500}
                  placeholder={t('profile.edit.bioPlaceholder')}
                />
                <p className="text-xs text-gray-400 text-right mt-1">{form.bio.length}/500</p>
              </div>
              <Select
                label={t('profile.edit.lookingFor')}
                value={form.intention}
                onChange={(e) => setForm({ ...form, intention: e.target.value })}
                options={INTENTIONS.map((i) => ({ value: i.value, label: t(i.labelKey) }))}
              />
              <div className="relative space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('profile.edit.city')}
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <MapPin size={16} />
                  </div>
                  <input
                    type="text"
                    value={cityQuery}
                    onChange={(e) => {
                      setCityQuery(e.target.value);
                      citySelectedRef.current = false;
                      if (form.city) {
                        setForm((prev) => ({
                          ...prev,
                          city: '',
                          cityPostalCode: '',
                          cityLat: null,
                          cityLng: null,
                        }));
                      }
                    }}
                    placeholder={t('profile.edit.cityPlaceholder')}
                    className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition"
                    data-testid="profile-city-input"
                  />
                </div>
                {showCityDropdown && citySuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                    {citySuggestions.map((s, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => selectCity(s)}
                        className="w-full px-4 py-3 text-left hover:bg-rose-50 dark:hover:bg-gray-800 transition flex items-center gap-2 text-sm dark:text-gray-200"
                        data-testid="profile-city-suggestion"
                      >
                        <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                        <span>{s.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.city && (
                  <div className="mt-1 p-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl flex items-center gap-2">
                    <MapPin
                      size={14}
                      className="text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                      {form.city}
                      {form.cityPostalCode ? ` (${form.cityPostalCode})` : ''}
                    </span>
                  </div>
                )}
              </div>
              {user.clan && (
                <div className="bg-purple-100 dark:bg-purple-900/30 rounded-xl p-3">
                  <div className="flex gap-2">
                    <Shield size={18} className="text-purple-600 dark:text-purple-400" />
                    <div>
                      <p className="font-semibold text-purple-700 dark:text-purple-400 text-sm">
                        {t('profile.edit.clanLabel', { clan: user.clan })}
                      </p>
                      <p className="text-xs text-purple-600 dark:text-purple-400">
                        {t('profile.edit.clanNote')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {isDating && (
                <div>
                  <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">
                    {t('profile.sections.lookingFor')}
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                    {user.intention ? (
                      <Badge c="rose">
                        {t(INTENTIONS.find((i) => i.value === user.intention)?.labelKey)}
                      </Badge>
                    ) : (
                      <span className="text-gray-400 text-sm">
                        {t('profile.sections.notSpecified')}{' '}
                        <button
                          onClick={() => setEditing(true)}
                          className="text-rose-500 dark:text-rose-400 underline"
                        >
                          {t('profile.sections.complete')}
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">
                    {t('profile.sections.about')}
                    <span className="relative inline-block ml-1.5 align-middle">
                      <button
                        onClick={() => setShowTooltip(showTooltip === 'bio' ? null : 'bio')}
                        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                        aria-label={t('profile.bio.visibilityNote')}
                      >
                        i
                      </button>
                      {showTooltip === 'bio' && (
                        <div className="absolute z-50 bottom-6 left-0 w-56 p-2 text-xs text-gray-300 bg-gray-800 border border-gray-700 rounded-lg shadow-lg">
                          {t('profile.bio.visibilityNote')}
                        </div>
                      )}
                    </span>
                  </h3>
                  {!editingBio && (
                    <button
                      onClick={() => {
                        setBioPrompts(user?.bioPrompts || []);
                        setEditingBio(true);
                      }}
                      title={t('common.edit')}
                      className="p-2 rounded-full text-gray-400 hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                    >
                      <PenLine size={16} />
                    </button>
                  )}
                </div>
                {editingBio ? (
                  <div className="space-y-3">
                    {bioPrompts.map((bp, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 relative"
                      >
                        <button
                          onClick={() => removeBioPrompt(idx)}
                          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 transition"
                          aria-label={t('common.delete')}
                        >
                          <X size={14} />
                        </button>
                        <select
                          value={bp.prompt}
                          onChange={(e) => {
                            const updated = [...bioPrompts];
                            updated[idx] = { ...updated[idx], prompt: e.target.value };
                            setBioPrompts(updated);
                          }}
                          className="w-full text-xs font-semibold text-gray-500 dark:text-gray-400 bg-transparent mb-2 focus:outline-none"
                        >
                          {BIO_PROMPTS.map((p) => (
                            <option key={p} value={p}>
                              {t(p)}
                            </option>
                          ))}
                        </select>
                        <textarea
                          value={bp.answer}
                          onChange={(e) => {
                            const updated = [...bioPrompts];
                            updated[idx] = { ...updated[idx], answer: e.target.value };
                            setBioPrompts(updated);
                          }}
                          maxLength={200}
                          rows={2}
                          className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm dark:text-gray-100 focus:outline-none focus:border-rose-300 resize-none"
                          placeholder={t('profile.bio.placeholder')}
                        />
                        <p className="text-xs text-gray-400 text-right mt-1">
                          {bp.answer.length}/200
                        </p>
                      </div>
                    ))}
                    {bioPrompts.length < 3 && (
                      <button
                        onClick={addBioPrompt}
                        className="flex items-center gap-1.5 text-sm text-rose-500 dark:text-rose-400 font-medium hover:underline"
                      >
                        <Plus size={14} />
                        {t('profile.bio.addPrompt')}
                      </button>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Btn size="sm" onClick={handleSaveBio} loading={loading} icon={Check}>
                        {t('common.save')}
                      </Btn>
                      <Btn v="ghost" size="sm" onClick={() => setEditingBio(false)}>
                        {t('common.cancel')}
                      </Btn>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                    {user.bioPrompts && user.bioPrompts.length > 0 ? (
                      <div className="space-y-3">
                        {user.bioPrompts.map((bp, idx) => (
                          <div key={idx}>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1">
                              {t(bp.prompt)}
                            </p>
                            <p className="text-gray-700 dark:text-gray-300 text-sm">{bp.answer}</p>
                          </div>
                        ))}
                      </div>
                    ) : user.bio ? (
                      <p className="text-gray-700 dark:text-gray-300 text-sm">{user.bio}</p>
                    ) : (
                      <span className="text-gray-400 text-sm">
                        {t('profile.sections.noDescription')}{' '}
                        <button
                          onClick={() => {
                            setBioPrompts([{ prompt: BIO_PROMPTS[0], answer: '' }]);
                            setEditingBio(true);
                          }}
                          className="text-rose-500 dark:text-rose-400 underline"
                        >
                          {t('profile.sections.add')}
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  {t('profile.sections.badges')}
                </h3>
                <div className="flex gap-1.5">
                  {user.badges?.map((b, i) => (
                    <Badge key={i} c="purple">
                      {b}
                    </Badge>
                  ))}
                </div>
              </div>
              {!isDating && userAge !== null && userAge >= 18 && (
                <div className="bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30 border border-rose-200 dark:border-rose-800 rounded-xl p-4 mt-2">
                  <div className="flex gap-3 items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-600 flex items-center justify-center flex-shrink-0">
                      <Heart size={18} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-rose-900 dark:text-rose-400 text-sm mb-1">
                        {t('profile.dating.title')}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                        {t('profile.dating.desc')}
                      </p>
                      <Btn
                        size="sm"
                        onClick={() => setShowActivateDating(true)}
                        data-testid="profile-btn-dating"
                      >
                        {t('profile.dating.button')}
                      </Btn>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Mes amis */}
      <Card className="mt-4 p-4" data-testid="profile-friends-section">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-sm dark:text-gray-200 flex items-center gap-2">
            <Users size={16} className="text-violet-500" />
            {t('profile.myFriends.title')} ({user.friends?.length || 0})
          </h3>
          {(user.friends?.length || 0) > 6 && (
            <Btn v="ghost" size="sm" onClick={() => navigate('/members')}>
              {t('profile.myFriends.seeAll')}
            </Btn>
          )}
        </div>
        {friendsLoading ? (
          <p className="text-sm text-gray-400">{t('common.loading')}</p>
        ) : friendProfiles.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-400 dark:text-gray-500 mb-3">
              {t('profile.myFriends.empty')}
            </p>
            <Btn v="outline" size="sm" onClick={() => navigate('/members')}>
              {t('profile.myFriends.discoverCta')}
            </Btn>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {friendProfiles.map((fp) => (
              <div key={fp.uid} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
                {fp.profilePicture ? (
                  <img
                    src={fp.profilePicture}
                    alt={fp.name}
                    className="w-12 h-12 rounded-full object-cover mx-auto mb-2"
                  />
                ) : (
                  <UserCircle size={48} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                )}
                <p className="font-semibold text-sm dark:text-gray-200 truncate">{fp.name}</p>
                {fp.city && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{fp.city}</p>
                )}
                <div className="flex justify-center gap-1 mt-2">
                  <button
                    onClick={() =>
                      navigate(`/messages/friend_${[user.uid, fp.uid].sort().join('_')}`)
                    }
                    data-testid="profile-friend-message"
                    className="p-1.5 text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-950/30 rounded-full transition"
                    aria-label={t('profile.myFriends.message')}
                  >
                    <MessageCircle size={16} />
                  </button>
                  <button
                    onClick={() => setRemovingFriendUid(fp.uid)}
                    data-testid="profile-friend-remove"
                    className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-full transition"
                    aria-label={t('profile.myFriends.remove')}
                  >
                    <UserMinus size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Centres d'intérêt */}
      <Card className="mt-4 p-4" data-testid="profile-interests-section">
        <div className="flex justify-between items-center mb-1">
          <h3 className="font-bold text-sm dark:text-gray-200">
            {t('profile.interests.title')}
            <span className="relative inline-block ml-1.5 align-middle">
              <button
                onClick={() => setShowTooltip(showTooltip === 'interests' ? null : 'interests')}
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                aria-label={t('profile.interests.visibilityNote')}
              >
                i
              </button>
              {showTooltip === 'interests' && (
                <div className="absolute z-50 bottom-6 left-0 w-56 p-2 text-xs text-gray-300 bg-gray-800 border border-gray-700 rounded-lg shadow-lg">
                  {t('profile.interests.visibilityNote')}
                </div>
              )}
            </span>
          </h3>
          {!editingInterests && (
            <button
              onClick={() => {
                setSelectedInterests(user?.interests || []);
                setEditingInterests(true);
              }}
              title={t('common.edit')}
              className="p-2 rounded-full text-gray-400 hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              <Pencil size={16} />
            </button>
          )}
        </div>
        {editingInterests ? (
          <div>
            {INTEREST_CATEGORIES.map((cat) => (
              <div key={cat.labelKey} className="mb-3">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                  {t(cat.labelKey)}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {cat.interests.map((interest) => (
                    <button
                      key={interest.value}
                      onClick={() => toggleInterest(interest.value)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition ${
                        selectedInterests.includes(interest.value)
                          ? 'bg-violet-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t(interest.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex gap-2 mt-3">
              <Btn size="sm" onClick={handleSaveInterests} loading={loading} icon={Check}>
                {t('common.save')} ({selectedInterests.length})
              </Btn>
              <Btn v="ghost" size="sm" onClick={() => setEditingInterests(false)}>
                {t('common.cancel')}
              </Btn>
            </div>
          </div>
        ) : user?.interests?.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {user.interests.map((v) => {
              const found = INTEREST_CATEGORIES.flatMap((c) => c.interests).find(
                (i) => i.value === v
              );
              return (
                <Badge key={v} c="purple">
                  {found ? t(found.labelKey) : v}
                </Badge>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400 dark:text-gray-500">
            {t('profile.interests.empty')}{' '}
            <button
              onClick={() => setEditingInterests(true)}
              className="text-violet-500 dark:text-violet-400 underline"
            >
              {t('profile.interests.add')}
            </button>
          </p>
        )}
      </Card>

      {/* Mon profil Rencontres */}
      {isDating && (
        <Card className="mt-4 p-4" data-testid="profile-dating-section">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-sm dark:text-gray-200 flex items-center gap-2">
              <Heart size={16} className="text-rose-500" />
              {t('profile.dating.sectionTitle')}
              <span className="relative inline-block ml-0.5">
                <button
                  onClick={() => setShowTooltip(showTooltip === 'dating' ? null : 'dating')}
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-[10px] text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                  aria-label={t('profile.dating.visibilityNote')}
                >
                  i
                </button>
                {showTooltip === 'dating' && (
                  <div className="absolute z-50 bottom-6 left-0 w-56 p-2 text-xs text-gray-300 bg-gray-800 border border-gray-700 rounded-lg shadow-lg">
                    {t('profile.dating.visibilityNote')}
                  </div>
                )}
              </span>
            </h3>
            {!editingDating && (
              <button
                onClick={() => {
                  setDatingForm({
                    lookingFor: user?.lookingFor || '',
                    intention: user?.intention || '',
                    height: user?.height ?? '',
                    education: user?.education || '',
                    profession: user?.profession || '',
                    languages: user?.languages || [],
                  });
                  setEditingDating(true);
                }}
                title={t('common.edit')}
                className="p-2 rounded-full text-gray-400 hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <Pencil size={16} />
              </button>
            )}
          </div>

          {editingDating ? (
            <div className="space-y-4">
              {/* lookingFor */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                  {t('profile.dating.lookingFor')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {LOOKING_FOR.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setDatingForm((p) => ({ ...p, lookingFor: opt.value }))}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                        datingForm.lookingFor === opt.value
                          ? 'bg-rose-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* intention */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                  {t('profile.dating.intention')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {INTENTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setDatingForm((p) => ({ ...p, intention: opt.value }))}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
                        datingForm.intention === opt.value
                          ? 'bg-rose-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t(opt.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              {/* height */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                  {t('profile.dating.height')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="100"
                    max="250"
                    value={datingForm.height}
                    onChange={(e) => {
                      const v = e.target.value.slice(0, 3);
                      setDatingForm((p) => ({ ...p, height: v }));
                    }}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      if (e.target.value && (n < 100 || n > 250)) {
                        setDatingForm((p) => ({
                          ...p,
                          height: n < 100 ? '100' : '250',
                        }));
                      }
                    }}
                    className="w-24 px-3 py-2 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition"
                    placeholder="170"
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {t('profile.dating.heightUnit')}
                  </span>
                </div>
              </div>

              {/* education */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                  {t('profile.dating.education')}
                </label>
                <select
                  value={datingForm.education}
                  onChange={(e) => setDatingForm((p) => ({ ...p, education: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition"
                >
                  <option value="">{t('profile.dating.indifferent')}</option>
                  {EDUCATION_LEVELS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              {/* profession */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                  {t('profile.dating.profession')}
                </label>
                <input
                  type="text"
                  maxLength={50}
                  value={datingForm.profession}
                  onChange={(e) => setDatingForm((p) => ({ ...p, profession: e.target.value }))}
                  placeholder={t('profile.dating.professionPlaceholder')}
                  className="w-full px-3 py-2.5 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition"
                />
              </div>

              {/* languages */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1.5">
                  {t('profile.dating.languages')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.value}
                      onClick={() => toggleLanguage(lang.value)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition ${
                        datingForm.languages.includes(lang.value)
                          ? 'bg-violet-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t(lang.labelKey)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Btn size="sm" onClick={handleSaveDating} loading={loading} icon={Check}>
                  {t('profile.dating.save')}
                </Btn>
                <Btn v="ghost" size="sm" onClick={() => setEditingDating(false)}>
                  {t('common.cancel')}
                </Btn>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.lookingFor')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.lookingFor
                      ? t(LOOKING_FOR.find((o) => o.value === user.lookingFor)?.labelKey)
                      : '—'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.intention')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.intention
                      ? t(INTENTIONS.find((o) => o.value === user.intention)?.labelKey)
                      : '—'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.height')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.height && user.height >= 100 && user.height <= 250
                      ? `${user.height} ${t('profile.dating.heightUnit')}`
                      : '—'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.education')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.education
                      ? t(EDUCATION_LEVELS.find((o) => o.value === user.education)?.labelKey)
                      : '—'}
                  </p>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('profile.dating.profession')}
                </p>
                <p className="text-sm font-medium dark:text-gray-200">{user.profession || '—'}</p>
              </div>
              {user.languages?.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.languages')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {user.languages.map((v) => {
                      const found = LANGUAGES.find((l) => l.value === v);
                      return (
                        <Badge key={v} c="purple">
                          {found ? t(found.labelKey) : v}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Préférences de matching */}
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
            <div className="flex justify-between items-center mb-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.dating.preferences')}
              </p>
              {!editingPrefs && (
                <button
                  onClick={() => {
                    const prefs = user?.datingPreferences || {};
                    setPrefsForm({
                      ageRange: prefs.ageRange || [18, 50],
                      maxDistance: prefs.maxDistance ?? '',
                      noDistanceLimit:
                        prefs.maxDistance === null || prefs.maxDistance === undefined,
                      heightRange: prefs.heightRange || [150, 200],
                      noHeightPref: prefs.heightRange === null || prefs.heightRange === undefined,
                      intentions: prefs.intentions || [],
                      education: prefs.education || '',
                    });
                    setEditingPrefs(true);
                  }}
                  title={t('common.edit')}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                >
                  <Pencil size={16} />
                </button>
              )}
            </div>

            {editingPrefs ? (
              <div className="space-y-4">
                {/* Age range */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                    {t('profile.dating.prefAge')}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="18"
                      max="70"
                      value={prefsForm.ageRange[0]}
                      onChange={(e) =>
                        setPrefsForm((p) => ({
                          ...p,
                          ageRange: [Number(e.target.value), p.ageRange[1]],
                        }))
                      }
                      className="w-20 px-3 py-2 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl dark:text-gray-100 focus:outline-none focus:border-rose-300"
                    />
                    <span className="text-gray-400">—</span>
                    <input
                      type="number"
                      min="18"
                      max="70"
                      value={prefsForm.ageRange[1]}
                      onChange={(e) =>
                        setPrefsForm((p) => ({
                          ...p,
                          ageRange: [p.ageRange[0], Number(e.target.value)],
                        }))
                      }
                      className="w-20 px-3 py-2 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl dark:text-gray-100 focus:outline-none focus:border-rose-300"
                    />
                    <span className="text-xs text-gray-400">{t('common.years')}</span>
                  </div>
                </div>

                {/* Distance max */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                    {t('profile.dating.prefDistance')}
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={prefsForm.noDistanceLimit}
                        onChange={(e) =>
                          setPrefsForm((p) => ({
                            ...p,
                            noDistanceLimit: e.target.checked,
                            maxDistance: e.target.checked ? '' : p.maxDistance,
                          }))
                        }
                        className="rounded"
                      />
                      {t('profile.dating.noLimit')}
                    </label>
                    {!prefsForm.noDistanceLimit && (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="5"
                          max="500"
                          value={prefsForm.maxDistance}
                          onChange={(e) =>
                            setPrefsForm((p) => ({ ...p, maxDistance: e.target.value }))
                          }
                          className="w-24 px-3 py-2 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl dark:text-gray-100 focus:outline-none focus:border-rose-300"
                          placeholder="100"
                        />
                        <span className="text-sm text-gray-400">km</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Height range */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                    {t('profile.dating.prefHeight')}
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={prefsForm.noHeightPref}
                        onChange={(e) =>
                          setPrefsForm((p) => ({ ...p, noHeightPref: e.target.checked }))
                        }
                        className="rounded"
                      />
                      {t('profile.dating.indifferent')}
                    </label>
                    {!prefsForm.noHeightPref && (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="100"
                          max="250"
                          value={prefsForm.heightRange[0]}
                          onChange={(e) =>
                            setPrefsForm((p) => ({
                              ...p,
                              heightRange: [Number(e.target.value), p.heightRange[1]],
                            }))
                          }
                          className="w-20 px-3 py-2 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl dark:text-gray-100 focus:outline-none focus:border-rose-300"
                        />
                        <span className="text-gray-400">—</span>
                        <input
                          type="number"
                          min="100"
                          max="250"
                          value={prefsForm.heightRange[1]}
                          onChange={(e) =>
                            setPrefsForm((p) => ({
                              ...p,
                              heightRange: [p.heightRange[0], Number(e.target.value)],
                            }))
                          }
                          className="w-20 px-3 py-2 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl dark:text-gray-100 focus:outline-none focus:border-rose-300"
                        />
                        <span className="text-xs text-gray-400">cm</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Intentions */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                    {t('profile.dating.prefIntentions')}
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {INTENTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => togglePrefIntention(opt.value)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full transition ${
                          prefsForm.intentions.includes(opt.value)
                            ? 'bg-rose-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                      >
                        {t(opt.labelKey)}
                      </button>
                    ))}
                  </div>
                  {prefsForm.intentions.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      {t('profile.dating.allIntentions')}
                    </p>
                  )}
                </div>

                {/* Education pref */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                    {t('profile.dating.prefEducation')}
                  </label>
                  <select
                    value={prefsForm.education}
                    onChange={(e) => setPrefsForm((p) => ({ ...p, education: e.target.value }))}
                    className="w-full px-3 py-2.5 bg-white dark:bg-gray-900 border-2 border-gray-100 dark:border-gray-700 rounded-xl dark:text-gray-100 focus:outline-none focus:border-rose-300"
                  >
                    <option value="">{t('profile.dating.indifferent')}</option>
                    {EDUCATION_LEVELS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(opt.labelKey)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-2">
                  <Btn size="sm" onClick={handleSavePrefs} loading={loading} icon={Check}>
                    {t('profile.dating.save')}
                  </Btn>
                  <Btn v="ghost" size="sm" onClick={() => setEditingPrefs(false)}>
                    {t('common.cancel')}
                  </Btn>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.prefAge')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.datingPreferences?.ageRange
                      ? `${user.datingPreferences.ageRange[0]} — ${user.datingPreferences.ageRange[1]}`
                      : '18 — 50'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.prefDistance')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.datingPreferences?.maxDistance
                      ? `${user.datingPreferences.maxDistance} km`
                      : t('profile.dating.noLimit')}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.prefHeight')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.datingPreferences?.heightRange
                      ? `${user.datingPreferences.heightRange[0]} — ${user.datingPreferences.heightRange[1]} cm`
                      : t('profile.dating.indifferent')}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('profile.dating.prefIntentions')}
                  </p>
                  <p className="text-sm font-medium dark:text-gray-200">
                    {user.datingPreferences?.intentions?.length > 0
                      ? user.datingPreferences.intentions
                          .map((v) => t(INTENTIONS.find((i) => i.value === v)?.labelKey))
                          .join(', ')
                      : t('profile.dating.allIntentions')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Utilisateurs bloqués */}
      {(user.blocked?.length > 0 || loadingBlocked) && (
        <Card className="mt-4 p-4">
          <h3 className="font-bold text-sm mb-3 dark:text-gray-200 flex items-center gap-2">
            <ShieldOff size={16} className="text-gray-400" />
            {t('profile.blocked.title')} ({user.blocked?.length || 0})
          </h3>
          {loadingBlocked ? (
            <p className="text-sm text-gray-400">{t('profile.blocked.loading')}</p>
          ) : (
            <div className="space-y-2">
              {blockedProfiles.map((bp) => (
                <div
                  key={bp.uid}
                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl p-3"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <img
                      src={bp.profilePicture}
                      alt={bp.name}
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="font-semibold text-sm dark:text-gray-200 truncate">{bp.name}</p>
                      <p className="text-xs text-gray-400 truncate">{bp.city}</p>
                    </div>
                  </div>
                  <Btn v="ghost" size="sm" onClick={() => handleUnblock(bp.uid)}>
                    {t('profile.blocked.unblock')}
                  </Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Mes annonces — masqué pour les mineurs */}
      {(userAge === null || userAge >= 18) && (
        <Card className="mt-4 p-4" data-testid="profile-annonces-section">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-sm dark:text-gray-200 flex items-center gap-2">
              <Megaphone size={16} className="text-rose-500" />
              {t('profile.annonces.title')} ({myAnnonces.length})
            </h3>
            <Btn
              v="outline"
              size="sm"
              onClick={() => {
                const activeCount = myAnnonces.filter((a) => a.status === 'active').length;
                if (activeCount >= ANNONCE_MAX_ACTIVE) {
                  toast(t('profile.annonces.limitReached', { max: ANNONCE_MAX_ACTIVE }), 'error');
                  return;
                }
                setEditingAnnonce(null);
                setShowAnnonceForm(true);
              }}
              data-testid="profile-new-annonce-btn"
            >
              {t('profile.annonces.newAnnonce')}
            </Btn>
          </div>
          {myAnnonces.length === 0 ? (
            <p className="text-sm text-gray-400">{t('profile.annonces.empty')}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {myAnnonces.map((a) => (
                <Card
                  key={a.id}
                  hover
                  onClick={() => navigate(`/annonces/${a.id}`)}
                  className="p-3"
                  data-testid="profile-annonce-card"
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-semibold text-sm dark:text-gray-200 truncate flex-1 mr-2">
                      {a.title}
                    </p>
                    <Badge c="rose">{t(getAnnonceCategoryLabel(a.category))}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 truncate">
                    {a.city}
                    {a.price ? ` · ${formatPrice(a.price)}` : ''}
                    {' · '}
                    {timeAgo(a.createdAt)}
                  </p>
                  <div className="flex items-center justify-between">
                    <Badge
                      c={
                        a.status === 'active' ? 'green' : a.status === 'expired' ? 'yellow' : 'red'
                      }
                    >
                      {a.status === 'active'
                        ? t('profile.annonces.statusActive')
                        : a.status === 'expired'
                          ? t('profile.annonces.statusExpired')
                          : t('profile.annonces.statusDeleted')}
                    </Badge>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Btn
                        v="ghost"
                        size="sm"
                        icon={Pencil}
                        onClick={() => {
                          setEditingAnnonce(a);
                          setShowAnnonceForm(true);
                        }}
                        aria-label={t('profile.annonces.editLabel')}
                      />
                      {a.status === 'expired' && (
                        <Btn
                          v="ghost"
                          size="sm"
                          icon={RefreshCw}
                          onClick={() => handleRenewAnnonce(a)}
                          aria-label={t('profile.annonces.renewLabel')}
                        />
                      )}
                      <Btn
                        v="ghost"
                        size="sm"
                        icon={Trash2}
                        className="text-red-500"
                        onClick={() => setDeletingAnnonceId(a.id)}
                        aria-label={t('profile.annonces.deleteLabel')}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>
      )}

      <ConfirmModal
        isOpen={!!removingFriendUid}
        onClose={() => setRemovingFriendUid(null)}
        onConfirm={handleRemoveFriend}
        title={t('profile.myFriends.removeConfirmTitle')}
        message={t('profile.myFriends.removeConfirmMessage', {
          name: friendProfiles.find((p) => p.uid === removingFriendUid)?.name || '',
        })}
        confirmLabel={t('profile.myFriends.remove')}
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={!!deletingAnnonceId}
        onClose={() => setDeletingAnnonceId(null)}
        onConfirm={handleDeleteAnnonce}
        title={t('profile.deleteAnnonce.title')}
        message={t('profile.deleteAnnonce.message')}
        confirmLabel={t('common.delete')}
        confirmVariant="danger"
      />

      <ActivateDatingModal
        isOpen={showActivateDating}
        onClose={() => setShowActivateDating(false)}
        user={user}
        onUpdate={onUpdate}
        toast={toast}
      />

      <AnnonceFormModal
        isOpen={showAnnonceForm}
        onClose={() => {
          setShowAnnonceForm(false);
          setEditingAnnonce(null);
        }}
        user={user}
        toast={toast}
        annonce={editingAnnonce}
        onSuccess={() => {
          setShowAnnonceForm(false);
          setEditingAnnonce(null);
          loadMyAnnonces();
        }}
      />
    </div>
  );
};

export default ProfilePage;
